// Explored squares per floor (qa-runs#1160; design/30 §3, design/32). What a sight group has explored is kept per
// floor: the ground floor's under the group's own key (`party`, or a character's token id) — so a scene with one
// floor, and the ground floor of a scene with several, keep exactly the record they had — and each other floor's under
// `<group>@<floorId>`. An eye explores the floor it stands on; the DM's cover and a new map clear every floor's.
//
// A player is told the explored squares of the floors their tokens are on only (`viewerExplored`): never another
// floor's. With no floors, nothing here changes anything.
//
// Floors are read loosely (`scene.floors`, `object.floor`, `wall.floor`) with the rules of `floors.ts`: a scene with
// one floor or none is one floor; a thing whose floor is absent or unknown is on the ground floor (the one marked
// `ground`, else the first).
import { normaliseSpans } from './fog.js';
/** The separator of a floor's key from its group's. Floor ids are `[a-z0-9-]{1,40}`. */
export const FLOOR_KEY_SEP = '@';
const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
/** An explored record's key: a group, optionally on a floor other than the ground. */
export const EXPLORED_KEY_RE = new RegExp(`^(party|${UUID})(@[a-z0-9-]{1,40})?$`, 'i');
const floorsOf = (state) => {
    const fs = state.scene?.floors;
    return Array.isArray(fs) ? fs : [];
};
/** The key a group's explored squares on a floor are kept under. */
export const exploredKey = (group, floor) => (floor === null ? group : `${group}${FLOOR_KEY_SEP}${floor}`);
/** A key's group and floor. */
export function splitKey(key) {
    const at = key.indexOf(FLOOR_KEY_SEP);
    return at < 0 ? { group: key, floor: null } : { group: key.slice(0, at), floor: key.slice(at + 1) };
}
/** Whether the scene has more than one floor: the only time explored squares are kept per floor. */
export const floored = (state) => floorsOf(state).length > 1;
/** The ground floor's id, or null with no floors. */
function groundId(state) {
    const fs = floorsOf(state);
    if (fs.length < 2)
        return null;
    return (fs.find((f) => f.ground) ?? fs[0]).id;
}
/** A thing's floor as explored squares read it: null for the ground floor (or no floors), else its floor's id. */
export function fogFloor(state, o) {
    if (!floored(state) || !o)
        return null;
    const f = o.floor;
    const g = groundId(state);
    if (typeof f !== 'string' || f === g || !floorsOf(state).some((x) => x.id === f))
        return null;
    return f;
}
/** The floors a player's tokens stand on: the ground floor with none (a late arrival, a watcher), as at one floor. */
export function heldFloors(state, viewerId) {
    const out = new Set();
    if (!floored(state))
        return new Set([null]);
    for (const o of Object.values(state.objects))
        if (o.owners.includes(viewerId))
            out.add(fogFloor(state, o));
    if (!out.size)
        out.add(null);
    return out;
}
const union = (lists) => normaliseSpans(lists.flat());
/**
 * A group's explored squares on one floor. Under each character's own sight, the party's are every group's on that
 * floor (as the unfloored rule unions them all).
 */
export function exploredOn(explored, group, floor, character) {
    const ex = explored ?? {};
    if (group === 'party' && character)
        return union(Object.entries(ex).filter(([k]) => splitKey(k).floor === floor).map(([, s]) => s));
    return ex[exploredKey(group, floor)] ?? [];
}
/**
 * What a player is told of their explored squares: `explored`, the ground floor's (with no floors, the scene's, as
 * before) — empty when no token of theirs is on the ground floor — and `exploredOn`, each other floor they hold, by its
 * id. ABSENT with no floors or none held above or below the ground. Nothing of a floor they do not hold.
 */
export function viewerExplored(state, groups, viewerId, character) {
    const ex = state.fog?.explored;
    const held = heldFloors(state, viewerId);
    const on = (f) => union(groups.map((g) => exploredOn(ex, g, f, character)));
    const explored = held.has(null) ? on(null) : [];
    const others = [...held].filter((f) => f !== null).sort();
    if (!others.length)
        return { explored };
    return { explored, exploredOn: Object.fromEntries(others.map((f) => [f, on(f)])) };
}
/**
 * Explored squares regrouped when the DM switches the sight setting (design/30 §3.3), floor by floor: to each
 * character's own, every character starts with the party's on each floor; back to the party's, the party's on each
 * floor is the union of every group's there.
 */
export function regroupExplored(explored, toCharacter, eyeIds) {
    const out = { ...explored };
    const floors = new Set(Object.keys(explored).map((k) => splitKey(k).floor));
    for (const f of floors) {
        if (toCharacter) {
            const party = explored[exploredKey('party', f)] ?? [];
            for (const id of eyeIds) {
                const k = exploredKey(id, f);
                out[k] = normaliseSpans([...(out[k] ?? []), ...party]);
            }
        }
        else {
            out[exploredKey('party', f)] = union(Object.entries(explored).filter(([k]) => splitKey(k).floor === f).map(([, s]) => s));
        }
    }
    return out;
}
//# sourceMappingURL=floorFog.js.map